import { useState } from 'react'
import Wizard from '@cloudscape-design/components/wizard'
import { StepBusinessType } from './wizard/StepBusinessType'
import { StepGoal } from './wizard/StepGoal'
import { StepChannels } from './wizard/StepChannels'
import { StepSchedule } from './wizard/StepSchedule'
import { StepPersona } from './wizard/StepPersona'
import { useAgentSetup } from '@/hooks/useAgentSetup'

interface WizardInitialValues {
  businessType: string
  goalType: string
  targetValue: string
  channels: string[]
  autoGenerate: boolean
  cadence: string
  timeLocal: string
  personaName: string
}

function localTimeToUtc(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const d = new Date()
  d.setHours(h, m, 0, 0)
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}

interface Props {
  clientId: string
  initialValues?: WizardInitialValues
  onComplete: () => void
  onCancel?: () => void
}

export function AgentSetupWizard({ clientId, initialValues, onComplete, onCancel }: Props) {
  const [activeStep, setActiveStep] = useState(0)
  const [businessType, setBusinessType] = useState(initialValues?.businessType ?? '')
  const [goalType, setGoalType] = useState(initialValues?.goalType ?? 'grow_referrals')
  const [targetValue, setTargetValue] = useState(initialValues?.targetValue ?? '10')
  const [channels, setChannels] = useState(initialValues?.channels ?? ['facebook_post', 'linkedin_post'])
  const [autoGenerate, setAutoGenerate] = useState(initialValues?.autoGenerate ?? true)
  const [cadence, setCadence] = useState(initialValues?.cadence ?? 'daily')
  const [timeLocal, setTimeLocal] = useState(initialValues?.timeLocal ?? '09:00')
  const [personaName, setPersonaName] = useState(initialValues?.personaName ?? 'Alex')
  const { setAgentGoal, upsertAgentPersona, updateClient, loading } = useAgentSetup()

  async function handleSubmit() {
    const deadline = new Date()
    deadline.setFullYear(deadline.getFullYear() + 1)

    await setAgentGoal({
      clientId,
      input: {
        goalType,
        targetMetric: goalType === 'grow_referrals' ? 'provider_replies_per_month'
          : goalType === 'increase_engagement' ? 'avg_engagement_rate'
          : goalType === 'expand_provider_network' ? 'new_providers_contacted'
          : 'launch_milestone',
        targetValue: parseFloat(targetValue) || 10,
        deadline: deadline.toISOString().slice(0, 10),
      },
    })

    await updateClient({
      id: clientId,
      selectedContentTypes: channels,
      schedulingConfig: autoGenerate
        ? { autoGenerate: { enabled: true, cadence, timeUtc: localTimeToUtc(timeLocal), contentTypes: channels } }
        : { autoGenerate: { enabled: false, contentTypes: [], cadence: 'daily', timeUtc: '00:00' } },
    })

    await upsertAgentPersona({
      clientId,
      input: {
        name: personaName || 'Alex',
        tone: 'friendly and professional',
        decisionStyle: 'data-driven',
        focusAreas: businessType ? [businessType] : [],
        experienceSummary: `${personaName || 'Alex'} is the marketing agent for this DME business.`,
      },
    })

    onComplete()
  }

  const steps = [
    {
      title: 'Business Type',
      content: <StepBusinessType value={businessType} onChange={setBusinessType} />,
    },
    {
      title: 'Marketing Goal',
      content: (
        <StepGoal
          goalType={goalType} targetValue={targetValue}
          onGoalTypeChange={setGoalType} onTargetValueChange={setTargetValue}
        />
      ),
    },
    {
      title: 'Channels',
      content: <StepChannels selected={channels} onChange={setChannels} />,
    },
    {
      title: 'Schedule',
      content: (
        <StepSchedule
          autoGenerateEnabled={autoGenerate} cadence={cadence} timeLocal={timeLocal}
          onAutoGenerateChange={setAutoGenerate} onCadenceChange={setCadence} onTimeLocalChange={setTimeLocal}
        />
      ),
    },
    {
      title: 'Agent Persona',
      content: <StepPersona personaName={personaName} onChange={setPersonaName} />,
    },
  ]

  return (
    <Wizard
      steps={steps}
      activeStepIndex={activeStep}
      onNavigate={e => setActiveStep(e.detail.requestedStepIndex)}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isLoadingNextStep={loading}
      i18nStrings={{
        stepNumberLabel: (s: number) => `Step ${s}`,
        collapsedStepsLabel: (s: number, t: number) => `Step ${s} of ${t}`,
        nextButton: 'Next',
        previousButton: 'Previous',
        submitButton: 'Finish Setup',
        cancelButton: 'Cancel',
      }}
    />
  )
}
